import type { ComponentType } from 'react';
import React, { useCallback, useMemo } from 'react';
import { EXTENSION, type Extension } from '@helpcenter/forge-ui-utils';
import { HELP_CENTER_LAYOUT_ELEMENT_MODULE } from '@helpcenter/forge-ui-utils/src/constants';
import { ExtensionProvider } from '@helpcenter/forge-ui-utils/src/extension-provider';
import { Renderer } from '@helpcenter/forge-ui-utils/src/renderer';
import { getWorkspaceAri } from '@helpcenter/meta';
import { v4 } from 'uuid';
import type { ADFEntity } from '@atlaskit/adf-utils/dist/types/types';
import type { ExtensionComponentProps, ExtensionManifest, MaybeADFEntity } from '@atlaskit/editor-common/extensions';
import { EnvironmentContext } from '@atlassian/forge-ui';
import type { ProductEnvironment } from '@atlassian/forge-ui/provider';
import { useExtensionList } from '@atlassian/forge-ui/provider';
import type { ExtensionNodeParameters } from '@atlassian/help-center-common-util/adf';
import { getApolloClient } from '@atlassian/help-center-common-util/apollo';

export type ExtensionRenderFunction = Promise<ComponentType<ExtensionComponentProps<ExtensionNodeParameters>>>;

export const useEditorExtensions = () => {
    const extensionType = HELP_CENTER_LAYOUT_ELEMENT_MODULE;
    const apolloClient = getApolloClient();
    const contextIds = [getWorkspaceAri()];

    const { extensions, error, loading } = useExtensionList({
        contextIds,
        type: extensionType,
        client: apolloClient,
    });

    const createExtensionProps = useCallback(
        (extensionId: string, extensionKey: string | undefined, extensionTitle: string): Promise<MaybeADFEntity> => {
            const localId = v4();
            const type = EXTENSION;
            const extensionsProps: ADFEntity = {
                type,
                attrs: {
                    extensionType,
                    localId,
                    extensionKey,
                    parameters: {
                        localId,
                        extensionId,
                        extensionTitle,
                    },
                },
            };
            return Promise.resolve(extensionsProps);
        },
        [extensionType]
    );

    const renderFunction = useCallback(
        (extension: Extension): ExtensionRenderFunction => {
            return Promise.resolve(({ node }) => (
                <EnvironmentContext.Provider value={extension.environmentType as ProductEnvironment}>
                    <ExtensionProvider extension={extension} noWrapper>
                        <Renderer
                            extension={extension}
                            extensionData={
                                node?.parameters && 'guestParams' in node.parameters && node.parameters.guestParams
                                    ? {
                                          ...node.parameters,
                                          type: extensionType,
                                          config: node.parameters.guestParams,
                                      }
                                    : {
                                          ...node.parameters,
                                          type: extensionType,
                                      }
                            }
                        />
                    </ExtensionProvider>
                </EnvironmentContext.Provider>
            ));
        },
        [extensionType]
    );

    const extensionManifests: Promise<ExtensionManifest[]> = useMemo(() => {
        return new Promise((resolve, reject) => {
            if (!loading) {
                if (error) {
                    reject(error);
                } else {
                    const editorExtensions: ExtensionManifest[] = (extensions as Extension[]).map((extension) => {
                        const { id, properties, key } = extension;
                        const { title } = properties;

                        return {
                            title,
                            type: extensionType,
                            key: extension.key || '',
                            icons: {
                                '48': () =>
                                    import(/* webpackChunkName: "editor-app-icons" */ '@atlaskit/icon/glyph/addon'),
                            },
                            description:
                                'description' in extension.properties ? extension.properties.description : undefined,
                            modules: {
                                quickInsert: [
                                    {
                                        key: 'quick-insert',
                                        action: () => createExtensionProps(id, key, title),
                                    },
                                ],
                                nodes: {
                                    default: {
                                        type: EXTENSION,
                                        render: () => renderFunction(extension),
                                    },
                                },
                            },
                        };
                    });
                    resolve(editorExtensions);
                }
            }
        });
    }, [extensions, error, loading, extensionType, createExtensionProps, renderFunction]);

    return extensionManifests;
};
